import { create } from "zustand";
import {
  applyConfigCommands,
  ConfigSlots,
  slotsToSavePayload,
} from "../util/configCommands";
import { importCsvAssetToJson } from "../util/csvToJson";
import { defaultConfigSlots } from "../util/defaultConfigSlots";
import {
  buildMenuDataFromCsvRows,
  clearUnavailableAccentColorSlots,
  getAvailableMenuData,
  getMenuCategorySubTitles,
  isMenuOptionCommandSelected,
  isMenuOptionSelected,
  MenuCategoryData,
  MenuData,
  MenuOptionData,
  MenuSubCategoryData,
} from "../util/menuDataFromCsv";

// Empty menu used before the CSV has been fetched and parsed.
const initialMenuData: MenuData = {
  title: "MODEL",
  subTitle: "Select your configurations",
  categories: [],
};

export type ConfiguratorDataMenuId = "exterior" | "interior";
export type ConfiguratorMenuId = ConfiguratorDataMenuId | "finalize";
type MenuDataById = Record<ConfiguratorDataMenuId, MenuData>;

const initialMenuDataById: MenuDataById = {
  exterior: initialMenuData,
  interior: initialMenuData,
};

const menuCsvPrefixes: Record<ConfiguratorDataMenuId, string> = {
  exterior: "ExteriorMenuData",
  interior: "InteriorMenuData",
};

const modelCsvSuffixes: Record<string, string> = {
  "21C": "C21",
  "21C Spyder": "Spyder",
  "21C VMax": "Vmax",
};

const modelConfigValues: Record<string, string> = {
  "21C": "21C",
  "21C Spyder": "Spyder",
  "21C VMax": "Vmax",
};

const carModelRenderCodes: Record<string, string> = {
  "21C": "02",
  "21C Spyder": "01",
  "21C VMax": "03",
};

const modelDefaultConfigSlots: Record<string, ConfigSlots> = {
  "21C": {
    ExteriorColour: "18",
    InteriorColour: "01",
  },
  "21C Spyder": {
    ExteriorColour: "04",
    InteriorColour: "03",
  },
  "21C VMax": {
    ExteriorColour: "07",
    InteriorColour: "10",
  },
};

function withMenuTitle(menuData: MenuData, modelName?: string): MenuData {
  return {
    ...menuData,
    title: modelName || initialMenuData.title,
  };
}

function getMenuCsvAssetPath(
  modelName: string,
  activeMenuId: ConfiguratorDataMenuId,
) {
  const csvPrefix = menuCsvPrefixes[activeMenuId];
  const csvSuffix = modelCsvSuffixes[modelName] || modelCsvSuffixes["21C"];

  return `/assets/${csvPrefix}_${csvSuffix}.csv`;
}

function getFirstOptionSlots(menuDataById: MenuDataById) {
  return (Object.values(menuDataById) as MenuData[]).reduce<ConfigSlots>(
    (slots, menuData) =>
      menuData.categories.reduce<ConfigSlots>(
        (categorySlots, category) =>
          category.subCategories.reduce<ConfigSlots>(
            (subCategorySlots, subCategory) =>
              subCategory.options.reduce<ConfigSlots>((optionSlots, option) => {
                const command = option.commandEntry;

                if (!command || optionSlots[command.slot]) {
                  return optionSlots;
                }

                return {
                  ...optionSlots,
                  [command.slot]: command.value,
                };
              }, subCategorySlots),
            categorySlots,
          ),
        slots,
      ),
    {},
  );
}

function getActiveMenuState(
  allMenuDataById: MenuDataById,
  configSlots: ConfigSlots,
  activeMenuId: ConfiguratorDataMenuId,
) {
  const allMenuData = allMenuDataById[activeMenuId];
  const menuData = getAvailableMenuData(allMenuData, configSlots);

  return {
    allMenuData,
    menuData,
    categorySubTitles: getMenuCategorySubTitles(menuData, configSlots),
  };
}

// Full Zustand state and actions used by the configurator menu.
type MenuStoreState = {
  selectedModelName?: string;
  activeMenuId: ConfiguratorMenuId;
  allMenuDataById: MenuDataById;
  allMenuData: MenuData;
  menuData: MenuData;
  configSlots: ConfigSlots;
  categorySubTitles: Record<string, string[]>;
  setSelectedModelName: (modelName?: string) => void;
  setActiveMenuId: (menuId: ConfiguratorMenuId) => void;
  loadMenuData: () => Promise<void>;
  isOptionSelected: (option: MenuOptionData) => boolean;
  selectOption: (
    category: MenuCategoryData,
    subCategory: MenuSubCategoryData,
    option: MenuOptionData,
  ) => void;
  getSelectedOptionsPayload: () => Record<string, string | undefined>;
};

// Central menu store: loads CSV data, applies selections/cascades, filters options, and saves.
export const useMenuStore = create<MenuStoreState>((set, get) => ({
  selectedModelName: undefined,
  activeMenuId: "exterior",
  allMenuDataById: initialMenuDataById,
  allMenuData: initialMenuData,
  menuData: initialMenuData,
  configSlots: {},
  categorySubTitles: {},
  setSelectedModelName: (modelName) => {
    set((state) => {
      const allMenuDataById: MenuDataById = {
        exterior: withMenuTitle(state.allMenuDataById.exterior, modelName),
        interior: withMenuTitle(state.allMenuDataById.interior, modelName),
      };
      const activeMenuState = getActiveMenuState(
        allMenuDataById,
        state.configSlots,
        state.activeMenuId === "finalize" ? "exterior" : state.activeMenuId,
      );

      return {
        selectedModelName: modelName,
        allMenuDataById,
        ...activeMenuState,
      };
    });
  },
  setActiveMenuId: (menuId) => {
    set((state) => {
      if (menuId === "finalize") {
        return {
          activeMenuId: menuId,
        };
      }

      return {
        activeMenuId: menuId,
        ...getActiveMenuState(state.allMenuDataById, state.configSlots, menuId),
      };
    });
  },

  // Loads both menu CSVs, applies default slot values, filters by prerequisites, and initializes UI state.
  loadMenuData: async () => {
    const { selectedModelName = "21C", activeMenuId } = get();
    const [exteriorRows, interiorRows] = await Promise.all([
      importCsvAssetToJson(getMenuCsvAssetPath(selectedModelName, "exterior")),
      importCsvAssetToJson(getMenuCsvAssetPath(selectedModelName, "interior")),
    ]);
    const allMenuDataById: MenuDataById = {
      exterior: withMenuTitle(
        buildMenuDataFromCsvRows(exteriorRows),
        selectedModelName,
      ),
      interior: withMenuTitle(
        buildMenuDataFromCsvRows(interiorRows),
        selectedModelName,
      ),
    };

    // Start each model load from Unreal's defaults and the model-specific defaults.
    const configSlots = {
      ...defaultConfigSlots,
      ...getFirstOptionSlots(allMenuDataById),
      ...modelDefaultConfigSlots[selectedModelName],
      Example: activeMenuId === "interior" ? "Interior" : "Exterior",
      CarModel:
        carModelRenderCodes[selectedModelName] || carModelRenderCodes["21C"],
      Environment: "01",
      "Variant Model":
        modelConfigValues[selectedModelName] || modelConfigValues["21C"],
    };
    const activeMenuState = getActiveMenuState(
      allMenuDataById,
      configSlots,
      activeMenuId === "finalize" ? "exterior" : activeMenuId,
    );

    set({
      allMenuDataById,
      ...activeMenuState,
      configSlots,
    });
  },

  // Lets UI components ask whether an option should appear selected.
  isOptionSelected: (option) => isMenuOptionSelected(option, get().configSlots),

  // Applies a clicked option's command and cascades, then recalculates the visible menu.
  selectOption: (category, subCategory, option) => {
    set((state) => {
      if (isMenuOptionCommandSelected(option, state.configSlots)) {
        return state;
      }

      const commands = [
        ...(option.commandEntry ? [option.commandEntry] : []),
        ...option.cascadeCommands,
      ];
      const commandedSlots = applyConfigCommands(state.configSlots, commands);
      const dataMenuId =
        state.activeMenuId === "finalize" ? "exterior" : state.activeMenuId;
      const activeAllMenuData = state.allMenuDataById[dataMenuId];
      const commandedMenuData = getAvailableMenuData(
        activeAllMenuData,
        commandedSlots,
      );
      const configSlots = clearUnavailableAccentColorSlots(
        commandedMenuData,
        commandedSlots,
      );
      const activeMenuState = getActiveMenuState(
        state.allMenuDataById,
        configSlots,
        dataMenuId,
      );

      return {
        configSlots,
        ...activeMenuState,
      };
    });
  },

  // Exposes the current config in the same "_Value" payload shape used for saving.
  getSelectedOptionsPayload: () => {
    return slotsToSavePayload(get().configSlots);
  },
}));

